import React, { useState, useEffect } from 'react';
import {
  Table, Button, Input, Select, Space, Modal, Form, Tag, Popconfirm,
  message, Card, Typography, Switch, Alert, Radio, Checkbox
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { useActionColumn } from '../utils/tableLayout';
const { Title, Text } = Typography;
const { Option } = Select;
const allowsAI = config => Boolean(config.enabled && config.isCollectable && (!config.status || config.status === 'active'));
const cleanKeywords = values => [...new Map((Array.isArray(values) ? values : [])
  .filter(value => typeof value === 'string').map(value => value.normalize('NFKC').trim()).filter(Boolean)
  .map(value => [value.toLocaleLowerCase('zh-CN'), value])).values()];
const triggerRules = config => {
  const keywords = cleanKeywords(config?.collectRules?.keywords);
  return {
    keywords: keywords.length ? keywords : ['姓名'],
    keywordMatchMode: config?.collectRules?.keywordMatchMode === 'all' ? 'all' : 'any',
    preferInterviewJob: config?.collectRules?.preferInterviewJob === true
  };
};

function GroupConfig() {
  const actionColumn = useActionColumn(160);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [robotError, setRobotError] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [robots, setRobots] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    enabled: '',
    robotName: ''
  });
  
  const [form] = Form.useForm();
  const watchedKeywords = Form.useWatch(['collectRules', 'keywords'], form);
  const watchedMatchMode = Form.useWatch(['collectRules', 'keywordMatchMode'], form);
  const { hasPermission, user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    loadConfigs();
  }, [pagination.current, pagination.pageSize, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSuperAdmin) {
      loadRobots();
    }
  }, [isSuperAdmin]);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      };
      
      const response = await api.get('/group-config', { params });
      setConfigs(response.data.configs);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total
      }));
    } catch (error) {
      console.error('加载报名处理规则失败:', error);
      if (error.response?.status === 401) {
        message.error('认证已过期，请重新登录');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        message.error('加载报名处理规则失败，请检查网络连接或重新登录');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRobots = async () => {
    try {
      const response = await api.get('/robot-settings', { 
        params: { limit: 1000, page: 1 } // 获取所有机器人
      });
      setRobots(response.data.robots || []);
    } catch (error) {
      setRobotError('接收机器人列表加载失败，请刷新页面后重试');
      console.error('加载机器人列表失败:', error);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      aiProcessingEnabled: true,
      collectRules: { keywords: ['姓名'], keywordMatchMode: 'any', preferInterviewJob: false }
    });
    setSaveError('');
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.resetFields();
    form.setFieldsValue({ groupName: record.groupName, worktoolRobotId: record.worktoolRobotId,
      aiProcessingEnabled: allowsAI(record), collectRules: triggerRules(record) });
    setSaveError('');
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/group-config/${id}`);
      message.success('删除成功');
      loadConfigs();
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      setSaveError('');
      const payload = { ...values, collectRules: { ...values.collectRules,
        keywords: cleanKeywords(values.collectRules?.keywords),
        preferInterviewJob: values.collectRules?.preferInterviewJob === true,
        keywordMatchMode: values.collectRules?.keywordMatchMode === 'all' ? 'all' : 'any' } };
      if (editingRecord) {
        await api.put(`/group-config/${editingRecord._id}`, payload);
        message.success('更新成功');
      } else {
        await api.post('/group-config', payload);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadConfigs();
    } catch (error) {
      setSaveError(error.response?.data?.message || '保存群组失败，请重试');
    } finally { setSaving(false); }
  };

  const handleBatchEnabled = async (enabled) => {
    try {
      await api.post('/group-config/batch/enabled', {
        ids: selectedRowKeys,
        enabled
      });
      message.success('批量设置启用状态成功');
      setSelectedRowKeys([]);
      loadConfigs();
    } catch (error) {
      message.error(error.response?.data?.message || '批量操作失败，请重试');
    }
  };

  const columns = [
    {
      title: '来源群',
      dataIndex: 'groupName',
      key: 'groupName',
      width: 210,
      ellipsis: true
    },
    {
      title: 'AI 提取条件',
      key: 'triggerRules',
      width: 260,
      render: (_, record) => {
        const rules = triggerRules(record);
        return <div>
          <Space size={[4, 4]} wrap>{rules.keywords.slice(0, 4).map(keyword => <Tag key={keyword}>{keyword}</Tag>)}</Space>
          {rules.keywords.length > 4 && <Text type="secondary">另有 {rules.keywords.length - 4} 个</Text>}
          <div className="cell-secondary">{rules.keywordMatchMode === 'all' ? '全部命中（且）' : '任意命中（或）'}</div>
          {rules.preferInterviewJob && <Tag color="blue">面试点优先</Tag>}
        </div>;
      }
    },
    {
      title: 'AI处理',
      key: 'enabled',
      width: 130,
      render: (_, record) => (
        <Tag color={allowsAI(record) ? 'green' : 'default'}>
          {allowsAI(record) ? '允许处理' : '已停用'}
        </Tag>
      )
    },
    isSuperAdmin && {
      title: '机器人ID',
      dataIndex: 'worktoolRobotId',
      key: 'worktoolRobotId',
      width: 120,
      ellipsis: true,
      render: (text) => text || '-'
    },
    isSuperAdmin && {
      title: '机器人名称',
      key: 'robotName',
      width: 120,
      ellipsis: true,
      render: (_, record) => {
        if (record.robotName) return record.robotName;
        if (record.robot && record.robot.name) return record.robot.name;
        return record.worktoolRobotId ? '未知机器人' : '-';
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (date) => date ? new Date(date).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作',
      key: 'action',
      ...actionColumn,
      render: (_, record) => (
        <Space>
          {hasPermission('groupConfig', 'write') && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              编辑
            </Button>
          )}
          {hasPermission('groupConfig', 'write') && (
            <Popconfirm
              title="确定删除这条报名处理规则吗？"
              onConfirm={() => handleDelete(record._id)}
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ].filter(Boolean);

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys
  };

  return (
    <div>
      <div className="page-header">
        <Title level={2}>报名处理规则</Title>
        <div className="description">按接收机器人、来源群和触发词决定哪些报名消息进入 AI 处理。</div>
      </div>

      <Alert type="info" showIcon message="消息命中已启用规则后，才会调用 AI 提取并继续匹配岗位。"
        description="来源群须与机器人收到的完整群名一致；触发词可设置为任意命中（或）或全部命中（且）。未命中的消息仍会保留原文和跳过原因。"
        style={{ marginBottom: 16 }} />

      <Card>
        <div className="table-toolbar">
          <div className="left">
            <Input.Search
              placeholder="搜索来源群"
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
            />
            <Select
              placeholder="启用状态"
              allowClear
              style={{ width: 120 }}
              onChange={(value) => handleFilterChange('enabled', value)}
            >
              <Option value="true">启用</Option>
              <Option value="false">禁用</Option>
            </Select>
            {isSuperAdmin && (
              <Select
                placeholder="选择机器人"
                allowClear
                style={{ width: 150 }}
                value={filters.robotName || undefined}
                onChange={(value) => handleFilterChange('robotName', value)}
              >
                {robots.map(robot => (
                  <Option key={robot._id} value={robot.name}>
                    {robot.name}
                  </Option>
                ))}
              </Select>
            )}
          </div>
          <div className="right">
            {hasPermission('groupConfig', 'write') && selectedRowKeys.length > 0 && (
              <Space wrap>
                <Button
                  onClick={() => handleBatchEnabled(true)}
                >
                  批量允许 AI 处理
                </Button>
                <Button
                  onClick={() => handleBatchEnabled(false)}
                >
                  批量停用 AI 处理
                </Button>
              </Space>
            )}

            {hasPermission('groupConfig', 'write') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                添加处理规则
              </Button>
            )}
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={configs}
          rowKey="_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          tableLayout="fixed"
          rowSelection={hasPermission('groupConfig', 'write') ? rowSelection : null}
        />
      </Card>

      {/* 添加/编辑模态框 */}
      <Modal
        title={editingRecord ? '编辑报名处理规则' : '添加报名处理规则'}
        open={modalVisible}
        onCancel={() => !saving && setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={saving}
        >
          {saveError && <Alert type="error" showIcon message={saveError} style={{ marginBottom: 16 }} />}
          {isSuperAdmin && <>
            {robotError && <Alert type="error" showIcon message={robotError} style={{ marginBottom: 16 }} />}
            <Form.Item name="worktoolRobotId" label="接收机器人" rules={[{ required: true, message: '请选择接收此群消息的机器人' }]}
              extra="同一个群名只对选中的接收机器人生效。列表为空时，请先在机器人设置中添加并启用接收机器人。">
              <Select showSearch optionFilterProp="label" placeholder="请选择接收机器人" options={robots.filter(robot => robot.isActive && ['receiver', 'both'].includes(robot.functionType)).map(robot => ({ value: robot.worktoolRobotId, label: `${robot.name}（${robot.worktoolRobotId}）` }))} />
            </Form.Item>
          </>}
          <Form.Item
            name="groupName"
            label="来源群名称"
            rules={[{ required: true, whitespace: true, message: '请输入来源群名称' }]}
            extra="填写完整群名，须与微信记录中的来源群名一致，不支持关键词或模糊匹配。"
          >
            <Input placeholder="请输入完整群名" maxLength={100} />
          </Form.Item>

          <Form.Item
            name={['collectRules', 'keywords']}
            label="AI 提取触发词"
            rules={[{ validator: (_, values) => {
              const keywords = cleanKeywords(values);
              if (!keywords.length) return Promise.reject(new Error('请至少添加一个 AI 提取触发词'));
              if (keywords.length > 20) return Promise.reject(new Error('最多添加 20 个 AI 提取触发词'));
              if (keywords.some(keyword => keyword.length > 50)) return Promise.reject(new Error('单个触发词不能超过 50 个字符'));
              return Promise.resolve();
            } }]}
            extra="输入一个词后按回车添加。系统按消息正文包含关系判断，不支持正则表达式。"
          >
            <Select mode="tags" aria-label="AI 提取触发词" placeholder="例如：姓名、岗位" tokenSeparators={[',', '，', '\n']}
              maxTagCount="responsive" open={false} />
          </Form.Item>

          <Form.Item
            name={['collectRules', 'keywordMatchMode']}
            label="触发词匹配方式"
            rules={[{ required: true, message: '请选择触发词匹配方式' }]}
          >
            <Radio.Group optionType="button" buttonStyle="solid" aria-label="触发词匹配方式">
              <Radio.Button value="any">任意命中（或）</Radio.Button>
              <Radio.Button value="all">全部命中（且）</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {cleanKeywords(watchedKeywords).length > 0 && <Alert type="info" showIcon style={{ marginBottom: 24 }}
            message={watchedMatchMode === 'all'
              ? `消息需要同时包含：${cleanKeywords(watchedKeywords).join('、')}`
              : `消息至少包含一个：${cleanKeywords(watchedKeywords).join('、')}`} />}

          <Form.Item name={['collectRules', 'preferInterviewJob']} valuePropName="checked"
            extra="仅对此来源群生效。有“面试点岗位”时使用该岗位及面试地址；没有时按普通岗位处理。面试点匹配不明确时保留待核对。">
            <Checkbox>优先使用面试点岗位</Checkbox>
          </Form.Item>

          <Form.Item
            name="aiProcessingEnabled"
            label="允许 AI 提取、匹配和推送"
            valuePropName="checked"
            extra="停用后，该群的新消息只保存原文，不进入 AI 处理流程。"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editingRecord ? '更新' : '创建'}
              </Button>
              <Button disabled={saving} onClick={() => setModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default GroupConfig;
